/**
 * FilterDropdown utility functions
 *
 * Extracted from FilterDropdown.tsx to improve maintainability and testability.
 * These utilities handle selection logic, group operations, and constraint enforcement.
 */

import type { FilterOption, GroupOption } from './types';

/**
 * Type guard to check if an option is a group
 *
 * @param option - Option to check
 * @returns true if option is a GroupOption with items
 */
export function isGroupOption(
  option: FilterOption | GroupOption
): option is GroupOption {
  return 'isGroup' in option && option.isGroup === true && 'items' in option;
}

/**
 * Enforces maxSelections constraint on a selection array.
 *
 * @param selections - Current selections
 * @param maxSelections - Maximum allowed selections
 * @returns Trimmed selections array
 *
 * @remarks
 * - For single-select (maxSelections=1), returns only the last selected item
 * - For multi-select with limit, implements FIFO queue (keeps last N items)
 * - Returns unchanged array if no limit or within limit
 *
 * @example
 * ```typescript
 * // Single-select: Only last item kept
 * enforceMaxSelections([opt1, opt2, opt3], 1) // [opt3]
 *
 * // Multi-select with limit: FIFO queue
 * enforceMaxSelections([opt1, opt2, opt3, opt4], 2) // [opt3, opt4]
 *
 * // No limit: unchanged
 * enforceMaxSelections([opt1, opt2], undefined) // [opt1, opt2]
 * ```
 */
export function enforceMaxSelections(
  selections: FilterOption[],
  maxSelections: number | undefined
): FilterOption[] {
  if (!maxSelections || selections.length <= maxSelections) {
    return selections;
  }

  // For single-select, last item replaces all (clear behavior)
  if (maxSelections === 1) {
    return [selections[selections.length - 1]];
  }

  // For multi-select with limit, keep last N (FIFO queue)
  return selections.slice(-maxSelections);
}

/**
 * Toggles selection for an individual item (non-group).
 *
 * @param prev - Previous selections
 * @param option - Option to toggle
 * @returns Updated selections
 *
 * @remarks
 * - If item is selected, removes it from selections
 * - If item is not selected, adds it to selections
 * - Does not enforce maxSelections (caller must apply enforceMaxSelections)
 */
export function toggleIndividualOption(
  prev: FilterOption[],
  option: FilterOption
): FilterOption[] {
  const isSelected = prev.some(item => item.id === option.id);

  if (isSelected) {
    // Remove the item
    return prev.filter(item => item.id !== option.id);
  } else {
    // Add the item
    return [...prev, option];
  }
}

/**
 * Toggles selection for all items in a group.
 *
 * @param prev - Previous selections
 * @param groupOption - Group option with items
 * @returns Updated selections
 *
 * @remarks
 * - If all group items are selected, removes all group items
 * - If any group item is not selected, selects all group items
 * - Does not add the group option itself (only its items)
 * - Does not enforce maxSelections (caller must apply enforceMaxSelections)
 */
export function toggleGroupOption(
  prev: FilterOption[],
  groupOption: GroupOption
): FilterOption[] {
  const selectableItems = groupOption.groupItems ?? groupOption.items;

  if (!selectableItems || selectableItems.length === 0) {
    return prev;
  }

  const groupSelected = selectableItems.every(item =>
    prev.some(s => s.id === item.id)
  );

  if (groupSelected) {
    // Remove all group items (don't add group itself)
    return prev.filter(
      item => !selectableItems.some(groupItem => groupItem.id === item.id)
    );
  } else {
    // Add all group items (don't add group itself)
    return [
      ...prev.filter(
        item => !selectableItems.some(groupItem => groupItem.id === item.id)
      ),
      ...selectableItems,
    ];
  }
}

/**
 * Flattens grouped options to individual items.
 * Used in single-select mode to remove group headers.
 *
 * @param options - Options array (may contain groups)
 * @returns Flat array of individual items only
 *
 * @remarks
 * - Groups are expanded to their items
 * - Individual items are kept as-is
 * - Group headers are removed from result
 *
 * @example
 * ```typescript
 * const options = [
 *   { id: 'opt1', label: 'Option 1', value: 'opt1' },
 *   { id: 'group1', label: 'Group 1', isGroup: true, items: [
 *     { id: 'opt2', label: 'Option 2', value: 'opt2' },
 *     { id: 'opt3', label: 'Option 3', value: 'opt3' }
 *   ]}
 * ];
 *
 * flattenOptions(options)
 * // [
 * //   { id: 'opt1', label: 'Option 1', value: 'opt1' },
 * //   { id: 'opt2', label: 'Option 2', value: 'opt2' },
 * //   { id: 'opt3', label: 'Option 3', value: 'opt3' }
 * // ]
 * ```
 */
export function flattenOptions(
  options: (FilterOption | GroupOption)[]
): FilterOption[] {
  return options.flatMap(option => {
    if (isGroupOption(option) && option.items) {
      return option.items;
    }
    return option;
  });
}

/**
 * Validates maxSelections prop value.
 *
 * @param maxSelections - Value to validate
 * @returns true if valid, false otherwise
 *
 * @remarks
 * Valid values:
 * - `undefined` (no limit)
 * - Positive integers (1, 2, 3, ...)
 *
 * Invalid values:
 * - Zero or negative numbers
 * - Non-integer numbers
 * - NaN or Infinity
 */
export function isValidMaxSelections(
  maxSelections: number | undefined
): boolean {
  if (maxSelections === undefined) return true;
  return Number.isInteger(maxSelections) && maxSelections >= 1;
}
